import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { apiFetch } from '@/lib/api/requests';
import { fetchRemoteConfig } from '@/lib/api/remoteConfig';

export interface TenantSummary {
  id: string;
  slug: string;
  name: string | null;
  role: string | null;
  status?: string | null;
  lastUsedAt?: string | null;
  isDemo?: boolean;
}

export interface TenantState {
  tenants: TenantSummary[];
  activeRestaurantId: string | null;
  activeSubdomain: string | null;
  activeRestaurantName: string | null;
  slug: string | null;
  isDemo: boolean;
  lastUsedAt: string | null;
  restaurant: { id: string; slug: string; name: string } | null;
  remoteConfig: {
    rushModeEnabled: boolean;
    inventoryDualTabEnabled: boolean;
  };

  setTenantRoster: (tenants: TenantSummary[]) => void;
  setActiveTenant: (restaurantId: string, subdomain: string, name?: string, isDemo?: boolean) => void;
  clearTenant: () => void;
  getActiveRestaurant: () => Promise<ActiveRestaurantDetails | null>;
  resolveActiveTenant: (hostname?: string) => Promise<void>;
  loadRemoteConfig: () => Promise<void>;
}

export interface ActiveRestaurantDetails {
  id: string;
  name: string;
  timezone: string;
  taxMode: string;
}

const sortByLastUsed = (a: TenantSummary, b: TenantSummary): number => {
  const aValue = a.lastUsedAt ? Date.parse(a.lastUsedAt) : 0;
  const bValue = b.lastUsedAt ? Date.parse(b.lastUsedAt) : 0;
  return bValue - aValue;
};

const DEFAULT_REMOTE_CONFIG = {
  rushModeEnabled: false,
  inventoryDualTabEnabled: false,
};

export const useTenantStore = create<TenantState>()(
  persist(
    (set, get) => ({
      tenants: [],
      activeRestaurantId: null,
      activeSubdomain: null,
      activeRestaurantName: null,
      slug: null,
      isDemo: false,
      lastUsedAt: null,
      restaurant: null,
      remoteConfig: {
        ...DEFAULT_REMOTE_CONFIG,
      },

      setTenantRoster: (tenants: TenantSummary[]) => {
        set((state) => {
          const existing = new Map(state.tenants.map((tenant) => [tenant.id, tenant]));
          const next = tenants.map((tenant) => {
            const previous = existing.get(tenant.id);
            return {
              id: tenant.id,
              slug: tenant.slug,
              name: tenant.name ?? tenant.slug,
              role: tenant.role ?? previous?.role ?? null,
              status: tenant.status ?? previous?.status ?? null,
              lastUsedAt: tenant.lastUsedAt ?? previous?.lastUsedAt ?? null,
              isDemo: tenant.isDemo ?? previous?.isDemo ?? false,
            };
          });

          const sorted = [...next].sort(sortByLastUsed);

          const activeTenant = state.activeRestaurantId
            ? sorted.find((tenant) => tenant.id === state.activeRestaurantId)
            : null;

          return {
            tenants: sorted,
            ...(activeTenant
              ? {
                  activeSubdomain: activeTenant.slug,
                  activeRestaurantName: activeTenant.name ?? activeTenant.slug,
                  restaurant: {
                    id: activeTenant.id,
                    slug: activeTenant.slug,
                    name: activeTenant.name ?? activeTenant.slug,
                  },
                }
              : {}),
          };
        });
      },

      setActiveTenant: (restaurantId: string, subdomain: string, name?: string, isDemo = false) => {
        const timestampMs = Date.now();
        const timestamp = new Date(timestampMs).toISOString();
        set((state) => {
          const nextTenants = state.tenants.reduce<TenantSummary[]>((acc, tenant) => {
            if (tenant.id === restaurantId) {
              acc.push({
                ...tenant,
                slug: subdomain,
                name: name ?? tenant.name ?? subdomain,
                lastUsedAt: timestamp,
                isDemo: isDemo ?? tenant.isDemo ?? false,
              });
              return acc;
            }
            acc.push(tenant);
            return acc;
          }, []);

          if (!nextTenants.some((tenant) => tenant.id === restaurantId)) {
            nextTenants.push({
              id: restaurantId,
              slug: subdomain,
              name: name ?? subdomain,
              role: null,
              status: null,
              lastUsedAt: timestamp,
              isDemo,
            });
          }

          const sorted = [...nextTenants].sort(sortByLastUsed);

          return {
            tenants: sorted,
            activeRestaurantId: restaurantId,
            activeSubdomain: subdomain,
            activeRestaurantName: name ?? null,
            slug: subdomain,
            isDemo,
            lastUsedAt: timestamp,
            restaurant: {
              id: restaurantId,
              slug: subdomain,
              name: name ?? subdomain,
            },
            remoteConfig: {
              ...DEFAULT_REMOTE_CONFIG,
            },
          };
        });

        void apiFetch('/tenant/usage', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
          body: JSON.stringify({ timestamp: timestampMs }),
        }).catch(() => {
          /* no-op */
        });

        void get().loadRemoteConfig();
      },

      clearTenant: () => {
        set((state) => ({
          tenants: state.tenants,
          activeRestaurantId: null,
          activeSubdomain: null,
          activeRestaurantName: null,
          slug: null,
          isDemo: false,
          lastUsedAt: null,
          restaurant: null,
          remoteConfig: {
            ...DEFAULT_REMOTE_CONFIG,
          },
        }));
      },

      getActiveRestaurant: async () => {
        const state = get();
        if (!state.activeRestaurantId) return null;

        try {
          const response = await apiFetch('/settings/restaurant', {
            headers: { Accept: 'application/json' },
          });

          if (!response.ok) {
            return null;
          }

          const result = (await response.json()) as {
            restaurant?: ActiveRestaurantDetails;
          };

          return result.restaurant ?? null;
        } catch (error) {
          console.error('Error loading active restaurant:', error);
          return null;
        }
      },

      resolveActiveTenant: async (hostname?: string) => {
        const state = get();
        const roster = state.tenants;

        if (!roster || roster.length === 0) {
          state.clearTenant();
          return;
        }

        const currentHost =
          hostname ?? (typeof window !== 'undefined' ? window.location.hostname : '');

        let subdomainFromUrl: string | null = null;
        if (currentHost.includes('.braisepos.com') || currentHost.includes('.lovable.app')) {
          const parts = currentHost.split('.');
          if (parts.length > 2 && parts[0] !== 'www') {
            subdomainFromUrl = parts[0];
          }
        }

        const selectTenant = (tenant: TenantSummary | undefined) => {
          if (tenant) {
            state.setActiveTenant(
              tenant.id,
              tenant.slug,
              tenant.name ?? tenant.slug,
              tenant.isDemo ?? false,
            );
          } else {
            state.clearTenant();
          }
        };

        if (subdomainFromUrl) {
          const matchBySlug = roster.find((tenant) => tenant.slug === subdomainFromUrl);
          if (matchBySlug) {
            selectTenant(matchBySlug);
            return;
          }
        }

        if (state.activeRestaurantId) {
          const existing = roster.find((tenant) => tenant.id === state.activeRestaurantId);
          if (existing) {
            return;
          }
        }

        const candidates = [...roster].sort(sortByLastUsed);
        selectTenant(candidates[0] ?? roster[0]);
      },
      loadRemoteConfig: async () => {
        try {
          const config = await fetchRemoteConfig();
          const features = config.features ?? {};
          const rushRaw =
            features.rush && typeof features.rush === 'object' && features.rush !== null
              ? (features.rush as Record<string, unknown>)
              : null;
          const rushModeFlat =
            typeof (features as Record<string, unknown>)['rush.mode'] === 'boolean'
              ? ((features as Record<string, unknown>)['rush.mode'] as boolean)
              : undefined;
          const rushModeEnabled = Boolean(rushRaw?.mode ?? rushModeFlat ?? false);
          const inventoryDualTabRaw =
            features.inventory_dual_tab && typeof features.inventory_dual_tab === 'object'
              ? (features.inventory_dual_tab as Record<string, unknown>)
              : null;
          const inventoryDualTabEnabled = Boolean(inventoryDualTabRaw?.enabled ?? false);
          set(() => ({
            remoteConfig: {
              ...DEFAULT_REMOTE_CONFIG,
              rushModeEnabled,
              inventoryDualTabEnabled,
            },
          }));
        } catch (error) {
          if (import.meta.env.DEV) {
            console.warn('[tenantStore] failed to load remote config', error);
          }
          set(() => ({
            remoteConfig: {
              ...DEFAULT_REMOTE_CONFIG,
            },
          }));
        }
      },
    }),
    {
      name: 'braise-tenant-store',
      partialize: (state) => ({
        tenants: state.tenants,
        activeRestaurantId: state.activeRestaurantId,
        activeSubdomain: state.activeSubdomain,
        activeRestaurantName: state.activeRestaurantName,
        slug: state.slug,
        isDemo: state.isDemo,
        lastUsedAt: state.lastUsedAt,
        restaurant: state.restaurant,
      }),
    },
  ),
);
export const useTenantSlug = () => useTenantStore((state) => state.slug ?? state.activeSubdomain);
